import CrudPage from "@/components/CrudPage";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Filas() {
  const { user: current } = useAuth();
  const isSuperadmin = current?.role === "superadmin";
  const [tenants, setTenants] = useState([]);
  const [ramais, setRamais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [mohRows, setMohRows] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/ramais").then(({ data }) => data || []).catch(() => []),
      api.get("/grupos").then(({ data }) => data || []).catch(() => []),
      api.get("/moh").then(({ data }) => data || []).catch(() => []),
      isSuperadmin
        ? api.get("/tenants").then(({ data }) => data || []).catch(() => [])
        : api.get("/tenants/current").then(({ data }) => data ? [data] : []).catch(() => []),
    ])
      .then(([extensions, ringGroups, mohList, tenantRows]) => {
        setRamais(extensions);
        setGrupos(ringGroups);
        setMohRows(mohList);
        setTenants(tenantRows);
      })
      .catch(() => {
        setRamais([]);
        setGrupos([]);
        setMohRows([]);
        setTenants([]);
      });
  }, [isSuperadmin]);

  const tenantById = Object.fromEntries((tenants || []).map((row) => [row.id, row]));
  const branchesForTenant = (tenantId) => tenantById[tenantId || current?.tenant_id]?.branches || [];
  const sameBranch = (row, form) => !form.branch_id || String(row.branch_id || "") === String(form.branch_id);
  const mohOptionsForTenant = (tenantId) => {
    const scopedTenantId = tenantId || current?.tenant_id || "";
    const tenant = tenantById[scopedTenantId] || null;
    const defaultMohUri = tenant?.default_moh_uri || "local_stream://moh";
    const options = [
      { value: defaultMohUri, label: "Padrao do cliente" },
      ...(mohRows || [])
        .filter((row) => row?.enabled !== false && (!scopedTenantId || row?.tenant_id === scopedTenantId))
        .map((row) => ({
          value: row.runtime_class ? `local_stream://${row.runtime_class}` : (row.audio_path || row.classe || "local_stream://moh"),
          label: row.nome || row.classe || "Musica em espera",
        })),
    ];
    const seen = new Set();
    return options.filter((option) => {
      const key = String(option.value || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fields = [
    ...(isSuperadmin ? [{
      key: "tenant_id",
      label: "Cliente",
      type: "select",
      full: true,
      options: [
        { value: "", label: "-- selecione o cliente --" },
        ...(tenants || []).map((row) => ({ value: row.id, label: row.nome })),
      ],
      onChange: (next) => ({ ...next, branch_id: "", ramal_fixo: "", grupo_fixo: "" }),
    }] : []),
    {
      key: "branch_id",
      label: "Unidade / filial",
      type: "select",
      full: true,
      options: (form) => [
        { value: "", label: "Escopo geral do cliente" },
        ...branchesForTenant(form.tenant_id).filter((branch) => branch.enabled !== false).map((branch) => ({
          value: branch.id,
          label: branch.nome || branch.codigo || branch.id,
        })),
      ],
      onChange: (next) => ({ ...next, ramal_fixo: "", grupo_fixo: "" }),
      hint: "Quando uma filial for escolhida, a fila somente podera usar ramais e grupos dessa filial.",
    },
    { key: "numero", label: "Numero", mono: true, placeholder: "700" },
    { key: "nome", label: "Nome" },
    {
      key: "modo_atendimento",
      label: "Modo de atendimento",
      type: "select",
      full: true,
      options: [
        { value: "agentes", label: "Fila por agentes do call center" },
        { value: "ramal_fixo", label: "Enviar para um ramal fixo" },
        { value: "grupo_fixo", label: "Enviar para um grupo de toque fixo" },
      ],
    },
    {
      key: "ramal_fixo",
      label: "Ramal fixo da fila",
      type: "select",
      full: true,
      hidden: (form) => form.modo_atendimento !== "ramal_fixo",
      options: (form) => [
        { value: "", label: "-- selecione o ramal fixo --" },
        ...ramais
          .filter((ramal) => ramal.enabled !== false && (!form.tenant_id || ramal.tenant_id === form.tenant_id) && sameBranch(ramal, form))
          .map((ramal) => ({
            value: ramal.numero,
            label: `${ramal.numero} - ${ramal.nome || "Sem nome"}`,
          })),
      ],
      hint: "Use esta opcao quando a fila precisar tocar sempre em um ramal especifico, sem depender de login de agente.",
    },
    {
      key: "grupo_fixo",
      label: "Grupo de toque fixo da fila",
      type: "select",
      full: true,
      hidden: (form) => form.modo_atendimento !== "grupo_fixo",
      options: (form) => [
        { value: "", label: "-- selecione o grupo de toque --" },
        ...grupos
          .filter((grupo) => grupo.enabled !== false && (!form.tenant_id || grupo.tenant_id === form.tenant_id) && sameBranch(grupo, form))
          .map((grupo) => ({
            value: grupo.numero,
            label: `${grupo.numero} - ${grupo.nome || "Sem nome"}`,
          })),
      ],
      hint: "Use esta opcao quando a fila precisar tocar sempre em um grupo de toque especifico, sem depender de login de agente.",
    },
    {
      key: "estrategia",
      label: "Estrategia",
      type: "select",
      hidden: (form) => form.modo_atendimento !== "agentes",
      options: [
        { value: "ring-all", label: "Todos" },
        { value: "round-robin", label: "Round robin" },
        { value: "longest-idle", label: "Mais ocioso" },
        { value: "top-down", label: "Ordem dos agentes" },
      ],
    },
    { key: "timeout", label: "Sem agente (segundos)", type: "number", hint: "Minimo operacional: 360 segundos.", hidden: (form) => form.modo_atendimento !== "agentes" },
    { key: "max_wait_time", label: "Espera maxima (segundos)", type: "number", hint: "Nao pode ser menor que o tempo sem agente." },
    { key: "max_simultaneous_calls", label: "Maximo de chamadas simultaneas", type: "number", hint: "0 = sem limite na fila." },
    {
      key: "musica",
      label: "Musica em espera",
      type: "select",
      full: true,
      hidden: (form) => form.modo_atendimento === "ramal_fixo",
      options: (form) => mohOptionsForTenant(form.tenant_id),
      hint: "Quem entrar na fila ouvira esta musica enquanto aguarda atendimento.",
    },
    { key: "service_level_seconds", label: "Meta de resposta (segundos)", type: "number", hidden: (form) => form.modo_atendimento !== "agentes" },
    { key: "service_level_target", label: "Meta SLA (%)", type: "number", hidden: (form) => form.modo_atendimento !== "agentes" },
    { key: "wrap_up_time", label: "Pos-atendimento (segundos)", type: "number", hidden: (form) => form.modo_atendimento !== "agentes" },
    { key: "discard_abandoned_after", label: "Descartar abandono apos (segundos)", type: "number", hidden: (form) => form.modo_atendimento !== "agentes" },
    {
      key: "fallback",
      label: "Destino alternativo (interno ou externo)",
      mono: true,
      hint: "Pode encaminhar para ramal, grupo, fila, URA ou para numero externo como 071XXXXXXXX, 55DDDNÚMERO ou +55DDDNÚMERO.",
    },
    { key: "enabled", label: "Ativo", type: "toggle" },
  ];

  return (
    <CrudPage
      title="Filas"
      subtitle="Call center nativo"
      endpoint="/filas"
      testidPrefix="filas"
      fields={fields}
      headers={["Numero", "Nome", "Filial", "Estrategia", "SLA", "Status"]}
      defaultValues={{
        tenant_id: isSuperadmin ? "" : (current?.tenant_id || ""),
        branch_id: "",
        numero: "",
        nome: "",
        modo_atendimento: "agentes",
        ramal_fixo: "",
        grupo_fixo: "",
        estrategia: "longest-idle",
        ramais: [],
        timeout: 360,
        max_wait_time: 360,
        max_simultaneous_calls: 0,
        service_level_seconds: 20,
        service_level_target: 80,
        wrap_up_time: 10,
        discard_abandoned_after: 60,
        musica: "local_stream://moh",
        fallback: "",
        enabled: true,
      }}
      transformFormOpen={(form) => ({
        ...form,
        ramais: [],
      })}
      transformSubmit={(form) => {
        const isFixedExtension = form.modo_atendimento === "ramal_fixo";
        const isFixedGroup = form.modo_atendimento === "grupo_fixo";
        const usesFixedTarget = isFixedExtension || isFixedGroup;
        const noAgentTimeout = usesFixedTarget ? Math.max(Number(form.timeout || 30), 30) : Math.max(Number(form.timeout || 360), 360);
        const maxWaitTime = Math.max(Number(form.max_wait_time || (usesFixedTarget ? 30 : 360)), noAgentTimeout);
        const maxSimultaneousCalls = Math.max(Number(form.max_simultaneous_calls || 0), 0);
        return {
          ...form,
          branch_id: String(form.branch_id || "").trim() || null,
          ramais: [],
          timeout: noAgentTimeout,
          max_wait_time: maxWaitTime,
          max_simultaneous_calls: maxSimultaneousCalls,
          ramal_fixo: isFixedExtension ? String(form.ramal_fixo || "").trim() : "",
          grupo_fixo: isFixedGroup ? String(form.grupo_fixo || "").trim() : "",
        };
      }}
      renderRow={(row) => (
        <>
          <td className="font-mono">{row.numero}</td>
          <td>{row.nome}</td>
          <td>{row.branch_name || "Escopo geral"}</td>
          <td>
            {row.modo_atendimento === "ramal_fixo"
              ? `Ramal fixo ${row.ramal_fixo || "-"}`
              : row.modo_atendimento === "grupo_fixo"
                ? `Grupo fixo ${row.grupo_fixo || "-"}`
                : row.estrategia}
          </td>
          <td className="font-mono">{row.service_level_target || 80}% / {row.service_level_seconds || 20}s</td>
          <td>{row.enabled ? "Ativo" : "Inativo"}</td>
        </>
      )}
    />
  );
}
